import { EtherFiLiquidityPoolAbi, EtherFiWeEthAbi } from '../abis/etherfi'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import {
  MULTICALL3_ADDRESS,
  Multicall3BalanceAbi,
  ONE_E18,
  toBigInt,
  type LstCall,
  type LstReader,
} from './shared'

/**
 * EtherFi weETH reader.
 *
 * Required calls: `weETH.totalSupply()` + `weETH.getRate()` (eETH per weETH).
 * When `readerExtras.etherFiLiquidityPool` is set, two more compute the real
 * instant-exit buffer: `Multicall3.getEthBalance(LP)` (the LP's free ETH) minus
 * `LP.ethAmountLockedForWithdrawal()` (ETH reserved for pending requests).
 *
 * We do **not** use `LiquidityPool.getTotalPooledEther()` — that's the
 * protocol's *total* pooled ETH (≈ TVL, ~1.87M ETH), ~100× the real buffer
 * (~1k ETH). `getRate()` returns eETH-per-weETH (rebasing underlying); we treat
 * eETH ≈ ETH for the rate surface — the peg is invariant by construction.
 */
export const readerEtherFiWeEth = (entry: LstRegistryEntry): LstReader => {
  const lp = entry.readerExtras?.etherFiLiquidityPool
  const calls: LstCall[] = [
    { address: entry.address, name: 'totalSupply', params: [] },
    { address: entry.address, name: 'getRate', params: [] },
  ]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const abis: any[] = [TotalSupplyAbi, EtherFiWeEthAbi]
  if (lp) {
    calls.push({
      address: MULTICALL3_ADDRESS,
      name: 'getEthBalance',
      params: [lp],
    })
    calls.push({
      address: lp,
      name: 'ethAmountLockedForWithdrawal',
      params: [],
    })
    abis.push(Multicall3BalanceAbi, EtherFiLiquidityPoolAbi)
  }
  return {
    calls,
    abis,
    parse: (slice) => {
      const totalSupply = toBigInt(slice[0])
      const exchangeRate = toBigInt(slice[1])
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      // Instant buffer = LP free ETH − ETH locked for pending withdrawals.
      // A failed read leaves liquidity undefined (orchestrator defaults to 0).
      let liquidity: bigint | undefined
      if (lp) {
        const lpBalance = toBigInt(slice[2])
        const locked = toBigInt(slice[3])
        if (lpBalance !== undefined && locked !== undefined) {
          liquidity = lpBalance > locked ? lpBalance - locked : 0n
        }
      }
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
        liquidity,
      }
    },
  }
}
